import React, { useState, useEffect } from 'react';
import { useParams, Link as ReactRouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Spinner,
  Button,
  VStack,
  HStack,
  Progress,
  useColorModeValue,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Breadcrumbs from './shared/Breadcrumbs';
import { useToast } from '../hooks/useToast';
import fetchWithAuth from '../utils/fetchWithAuth';

function PollResults() {
  const { pollId } = useParams();
  const [poll, setPoll] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [demographics, setDemographics] = useState(null);

  const bgColor = useColorModeValue('background.shade1');
  const color = useColorModeValue('theme.black');
  const boxBgColor = useColorModeValue('background.shade2');

  const toast = useToast();

  // Chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D'];

  const exportToCSV = () => {
    try {
      const results = calculateResults();

      // Create CSV header
      let csvContent = "Question,Choice,Vote Count,Percentage\n";

      // Add data rows
      results.forEach(result => {
        result.choices.forEach(choice => {
          const row = `"${result.question}","${choice.text}",${choice.count},${choice.percentage}%\n`;
          csvContent += row;
        });
        // Add blank row between questions
        csvContent += "\n";
      });

      // Add summary at the end
      csvContent += `\nTotal Unique Respondents,${getTotalResponses()}\n`;
      csvContent += `Poll Title,"${poll.title}"\n`;
      csvContent += `Poll State,${poll.state}\n`;

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `poll_results_${pollId}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.showSuccess('Success', 'Results exported to CSV');
    } catch (error) {
      console.error('Export error:', error);
      toast.showError('Error', 'Failed to export results');
    }
  };

  const fetchResults = async () => {
    try {
      setRefreshing(true);

      // Fetch poll data (includes questions and choices)
      const pollResponse = await fetchWithAuth(`/api/polls/${pollId}/`);
      if (!pollResponse.ok) throw new Error('Failed to fetch poll data');
      const pollData = await pollResponse.json();

      // Fetch responses for this poll
      const responsesResponse = await fetchWithAuth(`/api/responses/?question__poll=${pollId}`);
      if (!responsesResponse.ok) throw new Error('Failed to fetch responses');
      const responsesData = await responsesResponse.json();

      // Fetch demographics
      const demographicsResponse = await fetchWithAuth(`/api/polls/${pollId}/demographics/`);
      if (demographicsResponse.ok) {
        const demographicsData = await demographicsResponse.json();
        setDemographics(demographicsData);
      }

      setPoll(pollData);
      setResponses(responsesData.results || []);
      setLoading(false);
      setRefreshing(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const calculateResults = () => {
    if (!poll || !poll.questions) return [];

    return poll.questions.map(question => {
      // Count responses for each choice in this question
      const choiceCounts = {};
      let totalResponses = 0;

      // Initialize counts for all choices
      question.choices.forEach(choice => {
        choiceCounts[choice.id] = 0;
      });

      // Count actual responses
      responses.forEach(response => {
        if (response.question === question.id) {
          choiceCounts[response.choice] = (choiceCounts[response.choice] || 0) + 1;
          totalResponses++;
        }
      });

      // Build result object
      return {
        question: question.text,
        totalResponses,
        choices: question.choices.map(choice => ({
          text: choice.text,
          count: choiceCounts[choice.id] || 0,
          percentage: totalResponses > 0
            ? ((choiceCounts[choice.id] || 0) / totalResponses * 100).toFixed(1)
            : 0
        }))
      };
    });
  };

  const getTotalResponses = () => {
    // Count unique respondents (each respondent answers multiple questions)
    const uniqueRespondents = new Set(responses.map(r => r.respondent));
    return uniqueRespondents.size;
  };

  if (loading) {
    return (
      <Box bg={bgColor} color={color} minHeight="100vh" py={8}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Spinner size="xl" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg={bgColor} color={color} minHeight="100vh" py={8}>
        <Box maxWidth="800px" margin="0 auto" textAlign="center">
          <Text color="red.500">Error: {error}</Text>
          <Button as={ReactRouterLink} to="/pollsPage" mt={4}>
            Back to Polls
          </Button>
        </Box>
      </Box>
    );
  }

  if (!poll) {
    return (
      <Box bg={bgColor} color={color} minHeight="100vh" py={8}>
        <Box maxWidth="800px" margin="0 auto" textAlign="center">
          <Text>Poll not found</Text>
        </Box>
      </Box>
    );
  }

  const results = calculateResults();
  const totalResponses = getTotalResponses();

  return (
    <Box bg={bgColor} color={color} minHeight="100vh">
      <Box maxWidth="800px" margin="0 auto" px={4} py={8}>
        <Breadcrumbs
          items={[
            { label: 'Home', path: '/' },
            { label: 'Polls', path: '/pollsPage' },
            { label: poll.title, path: `/manage-questions/${pollId}` },
            { label: 'Results' }
          ]}
        />
        {/* Header */}
        <VStack spacing={4} align="stretch" mb={6}>
          <HStack justify="space-between">
            <Heading as="h1" size="xl">
              Poll Results
            </Heading>
            <HStack spacing={2}>
              <Button
                as={ReactRouterLink}
                to={`/analytics/${pollId}`}
                colorScheme="purple"
                size="sm"
                isDisabled={totalResponses === 0}
              >
                Advanced Analytics
              </Button>
              <Button
                onClick={exportToCSV}
                colorScheme="blue"
                size="sm"
                leftIcon={<DownloadIcon />}
                isDisabled={totalResponses === 0}
              >
                Export CSV
              </Button>
              <Button
                onClick={fetchResults}
                isLoading={refreshing}
                colorScheme="teal"
                size="sm"
              >
                Refresh
              </Button>
            </HStack>
          </HStack>

          <Box bg={boxBgColor} p={6} borderRadius="md" border="1px solid" borderColor="border.default">
            <Heading as="h2" size="lg" mb={2}>
              {poll.title}
            </Heading>
            {poll.description && (
              <Text fontSize="md" color="gray.600" mb={4}>
                {poll.description}
              </Text>
            )}
            <HStack spacing={6}>
              <Text fontWeight="bold">
                Status: <Text as="span" fontWeight="normal">{poll.state}</Text>
              </Text>
              <Text fontWeight="bold">
                Total Responses: <Text as="span" fontWeight="normal">{totalResponses}</Text>
              </Text>
            </HStack>
          </Box>
        </VStack>

        {/* Analytics Dashboard */}
        {totalResponses > 0 && (
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} mb={6}>
            <Stat bg={boxBgColor} p={4} borderRadius="md" border="1px solid" borderColor="border.default">
              <StatLabel>Total Responses</StatLabel>
              <StatNumber>{totalResponses}</StatNumber>
              <StatHelpText>Unique respondents</StatHelpText>
            </Stat>
            <Stat bg={boxBgColor} p={4} borderRadius="md" border="1px solid" borderColor="border.default">
              <StatLabel>Total Questions</StatLabel>
              <StatNumber>{poll.questions?.length || 0}</StatNumber>
              <StatHelpText>In this poll</StatHelpText>
            </Stat>
            <Stat bg={boxBgColor} p={4} borderRadius="md" border="1px solid" borderColor="border.default">
              <StatLabel>Avg Response Rate</StatLabel>
              <StatNumber>
                {results.length > 0
                  ? ((results.reduce((sum, r) => sum + r.totalResponses, 0) / (results.length * totalResponses)) * 100).toFixed(0)
                  : 0}%
              </StatNumber>
              <StatHelpText>Per question</StatHelpText>
            </Stat>
            <Stat bg={boxBgColor} p={4} borderRadius="md" border="1px solid" borderColor="border.default">
              <StatLabel>Poll Status</StatLabel>
              <StatNumber fontSize="lg">{poll.state}</StatNumber>
              <StatHelpText>{new Date().toLocaleDateString()}</StatHelpText>
            </Stat>
          </SimpleGrid>
        )}

        {/* Demographic Breakdown */}
        {demographics && demographics.total_respondents > 0 && Object.keys(demographics.demographics).length > 0 && (
          <Box mb={6}>
            <Heading as="h2" size="lg" mb={4}>
              Respondent Demographics
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {Object.entries(demographics.demographics).map(([field, values]) => (
                <Box
                  key={field}
                  bg={boxBgColor}
                  p={6}
                  borderRadius="md"
                  border="1px solid" borderColor="border.default"
                >
                  <Heading as="h3" size="md" mb={4} textTransform="capitalize">
                    {field.replace(/_/g, ' ')}
                  </Heading>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={values}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="value" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0088FE" />
                    </BarChart>
                  </ResponsiveContainer>
                  <VStack align="stretch" mt={4} spacing={2}>
                    {values.map((item, idx) => (
                      <HStack key={idx} justify="space-between">
                        <Text fontSize="sm">{item.value}:</Text>
                        <Text fontSize="sm" fontWeight="bold">
                          {item.count} ({item.percentage}%)
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Results */}
        {totalResponses === 0 ? (
          <Box bg={boxBgColor} p={8} borderRadius="md" border="1px solid" borderColor="border.default" textAlign="center">
            <Text fontSize="lg" color="gray.500">
              No responses yet. Results will appear here as people complete the survey.
            </Text>
          </Box>
        ) : (
          <VStack spacing={6} align="stretch">
            {results.map((result, index) => (
              <Box
                key={index}
                bg={boxBgColor}
                p={6}
                borderRadius="md"
                border="1px solid" borderColor="border.default"
              >
                <Heading as="h3" size="md" mb={4}>
                  {result.question}
                </Heading>
                {result.totalResponses === 0 ? (
                  <Text color="gray.500">No responses for this question</Text>
                ) : (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {/* Text and Progress Bars */}
                    <VStack spacing={3} align="stretch">
                      {result.choices.map((choice, choiceIndex) => (
                        <Box key={choiceIndex}>
                          <HStack justify="space-between" mb={1}>
                            <Text fontWeight={choice.count > 0 ? 'bold' : 'normal'}>
                              {choice.text}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {choice.count} {choice.count === 1 ? 'vote' : 'votes'} ({choice.percentage}%)
                            </Text>
                          </HStack>
                          <Progress
                            value={parseFloat(choice.percentage)}
                            size="sm"
                            colorScheme={choice.count > 0 ? 'teal' : 'gray'}
                            borderRadius="md"
                          />
                        </Box>
                      ))}
                      <Divider mt={2} />
                      <Text fontSize="sm" color="gray.600">
                        Total responses for this question: {result.totalResponses}
                      </Text>
                    </VStack>

                    {/* Pie Chart */}
                    <Box>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={result.choices.filter(c => c.count > 0).map(c => ({
                              name: c.text,
                              value: c.count
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={(entry) => `${entry.name}: ${entry.value}`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {result.choices.filter(c => c.count > 0).map((entry, idx) => (
                              <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </SimpleGrid>
                )}
              </Box>
            ))}
          </VStack>
        )}

        {/* Navigation */}
        <Box mt={8} textAlign="center">
          <Button as={ReactRouterLink} to="/pollsPage" variant="outline">
            Back to Polls
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default PollResults;
